<template>
  <Modal header-tag="h1" id="projects-create-modal" :show="show" @close="$emit('close')">
    <template #header><VisibleText>Create New Project</VisibleText></template>
    <div
      style="display: flex; width: 100%; align-items: center; justify-content: center"
      v-if="isLoading"
    >
      <span
        class="also-found__loading-spinner spinner"
        style="display: flex; height: 2rem; width: 2rem"
      ></span>
    </div>

    <!--------------------------------------------
    |
    | NO DATASETS
    |
    --------------------------------------------->
    <ProjectsCreateModalEmpty
      v-if="datasets.length == 0 && !isLoading"
      :parsedLicenses="parsedLicenses"
    />

    <!--------------------------------------------
    |
    | DATASETS AVAILABLE
    |
    --------------------------------------------->
    <form @submit.prevent novalidate v-else>
      <p class="error-msg__inline form-style-group-err" id="title-err" v-show="err.title">
        {{ err.title }}
      </p>
      <label for="project-name" class="form-style-group-label">
        <span class="form-style-group-text">
          <VisibleText>Title</VisibleText>
        </span>
        <div
          :class="['form-style-group-input-wrapper', { 'input-err': err.title }]"
          style="width: 40%"
        >
          <SvgIconDecorative icon="editAlt" id="create-modal-title-icon" />
          <input
            aria-describedby="title-err"
            required
            id="project-name"
            type="text"
            ref="title"
            :aria-invalid="err.title && !project.name ? 'true' : 'false'"
            v-model="project.name"
          />
        </div>
      </label>
      <fieldset class="form-fieldset-group form-style-group-fieldset" ref="dataset">
        <legend class="form-fieldset-group__legend form-style-group-fieldset-legend">
          <span class="form-style-group form-fieldset-err" v-show="err.datasets">
            {{ err.datasets }}
          </span>
          <strong><VisibleText>Select Dataset</VisibleText></strong>
        </legend>
        <div class="create-modal__details" style="display: flex; flex-direction: row; width: 100%">
          <VisibleText>Choose a dataset for this project.</VisibleText>
          <div style="flex-direction: row; margin-right: 2rem">
            <div style="display: flex">
              <ButtonIconOnly
                :icon="projectSortOrder == 'des' ? 'descending' : 'ascending'"
                style="
                  width: 2rem;
                  height: 2rem;
                  border-radius: 5px;
                  color: black;
                  margin-right: 1rem;
                "
                class="projects__view-select-btn"
                @click-handler="changeSortOrder"
              >
                <div v-if="projectSortOrder == 'des'">
                  <VisibleText>newest first</VisibleText>
                </div>
                <div v-else>
                  <VisibleText>oldest first</VisibleText>
                </div>
              </ButtonIconOnly>
              <div
                style="width: 100%"
                v-if="
                  permissions.includes('create-dataset') &&
                  (!this.parsedLicenses ||
                    !(this.parsedLicenses.length == 1 && this.parsedLicenses.includes('demo')))
                "
              >
                <LinkIconOnly
                  class="create-modal__details-upload"
                  href="/dataset"
                  id="create-modal-upload-dataset"
                  icon="upload"
                >
                  <VisibleText>Upload dataset</VisibleText>
                </LinkIconOnly>
              </div>
            </div>
          </div>
        </div>
        <ProjectsCreateModalDatasets
          :datasets="datasets"
          :pre-selected="localPreSelected"
          @selectedDataset="selectedDataset"
        />
      </fieldset>
    </form>
    <template #footer>
      <button class="form-btn" type="submit" @click="createProject" v-if="datasets.length > 0">
        <VisibleText>Create Project</VisibleText>
      </button>
      <button class="form-btn-secondary" @click="$emit('close')">
        <VisibleText>Close</VisibleText>
      </button>
    </template>
  </Modal>
</template>

<script>
// Components
import LinkIconOnly from '@/components/UI/Link/LinkIconOnly.vue';
import Modal from '@/components/UI/Modal.vue';
import ProjectsCreateModalDatasets from './ProjectsCreateModalDatasets.vue';
import SvgIconDecorative from '@/components/UI/Svg/SvgIcon';
import ButtonIconOnly from '@/components/UI/Button/ButtonIconOnly.vue';

// Models
import BenchmarkingProject from '@/models/BenchmarkingProjectModel';
import BenchmarkingDataset from '@/models/BenchmarkingDatasetModel.js';

// Mixins
import UserMixin from '@/utils/mixins/userMixin.js';

// Does the create model empty work? If so, the datasetuploaderreupload todo may be fine
export default {
  name: 'ProjectsCreateModal',
  mixins: [UserMixin],
  components: {
    LinkIconOnly,
    Modal,
    ProjectsCreateModalDatasets,
    ProjectsCreateModalEmpty: () => import('./ProjectsCreateModalEmpty'),
    SvgIconDecorative,
    ButtonIconOnly,
  },
  props: {
    show: {
      defult: () => false,
      type: Boolean,
    },
    preSelected: {
      default: () => null,
      type: String,
    },
    refresh: {
      default: () => false,
      type: Boolean,
    },
  },
  data() {
    return {
      project: {},
      localPreSelected: '',
      err: {
        title: null,
        datasets: null,
      },
      datasets: [],
      isLoading: false,
      projectSortOrder: 'des',
    };
  },
  created() {
    this.refreshDatasetProjects();
    if (this.preSelected) {
      this.localPreSelected = this.preSelected;
    }
  },
  methods: {
    changeSortOrder() {
      if (this.projectSortOrder == 'des') {
        this.projectSortOrder = 'asc';
      } else {
        this.projectSortOrder = 'des';
      }
      this.sortDatasets();
    },
    async sortDatasets() {
      const data = await this.$pigeonline.projects.get(BenchmarkingDataset, {});
      if (data.length) {
        if (this.projectSortOrder == 'asc') {
          this.datasets = data.sort(
            (a, b) => new Date(a.last_modified_on.$date) - new Date(b.last_modified_on.$date),
          );
        } else {
          this.datasets = data.sort(
            (a, b) => new Date(b.last_modified_on.$date) - new Date(a.last_modified_on.$date),
          );
        }
        this.$emit('foundDatasets', this.datasets);
      }
      this.checkSelectedTitle();
    },
    async createProject() {
      this.clearErrs();
      if (!this.validate()) return;
      this.$emit('close');
      const newProject = await this.$pigeonline.projects.create(this.project);
      this.$router.push({
        path: `/project/${newProject.id}/analysis/`,
      });
    },
    async refreshDatasetProjects() {
      this.isLoading = true;
      // TODO modern JS axios is converting this into non-JSON format which our BE isn't picking up. We are not using it and sorting on the FE anyway.
      // const data = await this.$pigeonline.projects.get(BenchmarkingDataset, {
      //   sort: {
      //     last_modified_on: "desc"
      //   }
      // })
      // const data = await this.$pigeonline.projects.get(BenchmarkingDataset, {})

      // if (data.length === 0) return
      // sort by completed
      // this.datasets = data.sort((a, b) => {
      //   if (
      //     a.currStatusText() === "complete" &&
      //     b.currStatusText() !== "complete"
      //   )
      //     return -1
      //   if (
      //     b.currStatusText() === "complete" &&
      //     a.currStatusText() !== "complete"
      //   )
      //     return 1
      //   return 0
      // })
      this.sortDatasets();
      this.isLoading = false;
    },
    clearErrs() {
      this.err.title = null;
      this.err.datasets = null;
    },
    selectedDataset(datasetId) {
      this.localPreSelected = datasetId;
      this.checkSelectedTitle();
    },
    validate() {
      let firstErr = null;
      if (this.project.name.length == 0) {
        this.err.title = 'Error: project title is required';
        if (!firstErr) firstErr = this.$refs['title'];
      }
      if (!this.project.datasets || this.project.datasets.length === 0) {
        this.err.datasets = 'Error: please select at least one dataset';
        if (!firstErr) firstErr = document.getElementsByClassName('create-modal__dataset-input')[0];
      }
      if (firstErr) firstErr.focus();
      return !firstErr;
    },
    async checkSelectedTitle() {
      let selectedDataset = this.datasets.filter(
        (dataset) =>
          dataset.dataset_id == this.localPreSelected || dataset.id == this.localPreSelected,
      );
      if (selectedDataset && selectedDataset[0]) {
        // this.project.title = selectedDataset[0].name
        const ds = selectedDataset[0];
        const date = new Date().toISOString().slice(0, 10); // "2026-02-12"

        const autoName = `Project from: ${ds.name} - ${date}`;
        this.project = await new BenchmarkingProject({ name: autoName });
        this.project.title = autoName;

        this.project.addDataset(this.localPreSelected);
        this.project.updateStatus('datasetSelected');
      }
    },
  },
  watch: {
    refresh: function (val) {
      if (val) this.loadData();
    },
    preSelected: function (val) {
      if (val) {
        this.localPreSelected = val;
        this.checkSelectedTitle();
      }
    },
  },
};
</script>
